<template>
  <nx-fullscreen-dialog
    :active="active"
    :title="$t('message.wizard.new.' + entity)"
    @dismiss="dismiss"
  >
    <template slot="toolbar-items">
      <v-btn
        v-if="!enableCreateAndContinue"
        @click="create(true)"
        color="accent"
        dark
        flat
        :loading="loading"
        :disabled="!flags.valid || !validUpload || loading"
      >{{ $t('message.wizard.createBtn') }}</v-btn>

      <template v-else>
        <v-btn
          v-if="features.CREATE_AND_CONTINUE_WIZARD_BUTTON"
          @click="create(false)"
          color="accent"
          dark
          flat
          :loading="loading"
          :disabled="!flags.valid || !validUpload || loading"
        >{{ $t('message.wizard.createAndContinueBtn') }}</v-btn>
        <v-btn
          @click="create"
          color="accent"
          dark
          flat
          :loading="loading"
          :disabled="!flags.valid || !validUpload || loading"
        >{{ editTextBtn }}</v-btn>
      </template>
    </template>

    <v-container py-3 fluid class="nx-taxonomy-wizard-body">
      <v-layout wrap fill-height v-if="active">
        <v-flex class="md6 offset-md3 xs12">
          <nx-form-scope-generator
            ref="form"
            class="nx-form"
            :value="draft"
            @input="updateDraft"
            @form="flags = $event"
            @submit.stop.prevent
            :hide-heading="hideScopeHeading"
            :scope="validationScope"
            :config="computedConfig"
            :title="config.title"
          >
            <nx-taxonomy-form-selector
              :auth="taxonomyAuth"
              @authorized="onTaxonomyAuthorized"
              @input="onTaxonomyInput"
              v-model="draft.mainTaxonomyId"
              validation="required"
              v-bind="taxonomyFieldParams"
            ></nx-taxonomy-form-selector>
          </nx-form-scope-generator>

          <v-tooltip v-if="autoPublish" left :disabled="!autoPublishDisabled">
            <v-switch
              :disabled="autoPublishDisabled"
              :label="$t('message.content.tools.publish')"
              ref="publish"
              slot="activator"
              v-model="draft.autopublish"
            ></v-switch>
            <span
              v-if="autoPublishErrorReason"
              v-html="$t('message.content.auth.publish.' + autoPublishErrorReason )"
            ></span>
          </v-tooltip>
          <nx-upload-file
            ref="upload"
            :label="$t(`message.wizard.${entity}`)"
            name="file"
            :data="creationPayload"
            :options="options"
            @newFile="onFileSelected"
            @load="onLoad"
            @success="onSuccess"
          ></nx-upload-file>
        </v-flex>
      </v-layout>
    </v-container>
  </nx-fullscreen-dialog>
</template>

<script>
import { mapGetters } from 'vuex';
import NxConfigWizard from '@/modules/entity/wizard/ConfigWizard';
import UploadFile from '@/modules/upload/UploadFile';
import authorizable from '@/modules/authorization/mixins/authorizable';
import cloneDeep from 'lodash/cloneDeep';
import wizardConfig from '@/modules/content/wizard/config/wizard';
import { getImageAuthor, getImageDescription } from '@/utils/exif';

export default {
  name: 'nx-entity-file-upload-wizard',
  extends: NxConfigWizard,
  mixins: [authorizable],
  components: {
    NxUploadFile: UploadFile,
  },
  provide() {
    return {
      $parentForm: {
        scope: 'file-upload-wizard',
        updateField() {},
      },
    };
  },
  data() {
    return {
      autoPublishDisabled: false, // Will change depending of auth over selected taxonomy
      autoPublishErrorReason: '',
      navigateToEntityEditor: true,
      draft: { autopublish: true },
      flags: {},
    };
  },
  computed: {
    ...mapGetters(['features']),
    editTextBtn() {
      if (this.features.CREATE_AND_CONTINUE_WIZARD_BUTTON) {
        return this.$t('message.wizard.createAndEditBtn', {
          type: this.$t(`message.wizard.${this.entity}`),
        });
      }
      return this.$t('message.wizard.createBtn');
    },
    fileUploadConfig() {
      return (
        (
          this.config.fieldsConfig.find(
            item => item.component === 'FileUpload',
          ) || {}
        ).params || {}
      );
    },
    autoPublish() {
      return !!this.fileUploadConfig.autoPublish;
    },
    apiPath() {
      return this.fileUploadConfig.apiPath || null;
    },
    mimeType() {
      return this.fileUploadConfig.mimeType || null;
    },
    maxFileSize() {
      return this.fileUploadConfig.maxFileSize || null;
    },
    creationPayload() {
      return {
        ...this.draft,
        ...this.additionalPayload,
        context: this.context,
      };
    },
    computedConfig() {
      const filteredConfig = cloneDeep(this.config);
      filteredConfig.fieldsConfig = filteredConfig.fieldsConfig
        .filter(item => item.component !== 'TaxonomySelectorField')
        .filter(item => item.component !== 'FileUpload');
      return filteredConfig;
    },
    taxonomyAuth() {
      return {
        actions: {
          ADD: 'create', // Specify bbnx operation when adding taxonomies for validation
        }, // Object of params that will be sent to bbnx to validate action
        params: {
          entity: 'content',
          type: this.entity,
        },
      };
    },
    taxonomyFieldParams() {
      const defaultParams = {
        name: 'mainTaxonomyId',
        label: this.$t('message.wizard.category'),
        limit: 1,
        type: wizardConfig.config.mainTaxonTypes,
        additionalFilters: { archived: 'no' },
      };

      const config = this.config.fieldsConfig.find(
        item => item.component === 'TaxonomySelectorField',
      );
      if (!config) {
        return defaultParams;
      }
      return { ...defaultParams, ...config.params };
    },
    validUpload() {
      return this.$refs.upload.valid && this.$refs.upload.files.length;
    },
    options() {
      return {
        maximum: 1,
        paramName: 'file',
        url: this.apiPath || `/api/content/${this.entity}`,
        accept: this.mimeType,
        size: this.maxFileSize || 100 * 1000000, // 100 Mb
        focalPointEnabled: this.features.IMAGE_FOCAL_POINT && this.entity === 'image',
      };
    },
  },
  methods: {
    create(navigateToEntityEditor) {
      this.navigateToEntityEditor = navigateToEntityEditor !== false;
      if (this.$refs.upload.valid) {
        this.$refs.upload.submit();
      }
    },
    onLoad(loading) {
      if (loading) {
        this.loadingStart();
      } else {
        this.loadingFinish();
      }
    },
    onSuccess(file) {
      const response = file.response.response;
      const entity = {
        ...response,
        route: {
          name: 'content.editor',
          params: { type: response.type, id: response.id },
        },
        navigateToEntityEditor: this.navigateToEntityEditor,
      };
      this.$emit('entityCreated', entity);
      this.toast.notify(this.$t('message.wizard.createSuccess'));
      this.$emit('dismiss');
      this.loadingFinish();
    },
    onTaxonomyAuthorized(value) {
      if (!value) {
        this.toast.notify('message.content.auth.create.taxonomy');
      }
    },
    async onTaxonomyInput(value) {
      // If is currently disabled by content or type, omit taxonomy authorization
      if (
        this.autopublishErrorReason
        && this.autopublishErrorReason !== 'category'
      ) {
        return;
      }

      // If there isn't value, set autopublish disabled and true
      if (!value) {
        this.draft.autopublish = true;
        this.autopublishDisabled = false;
        return;
      }

      // Check if use can publish on current section
      const canCreateOnTaxonomy = await this.can('create', {
        value,
        entity: 'content',
        type: this.entity,
      });
      const canPublishOnTaxonomy = await this.can('publish', {
        value,
        entity: 'content',
        type: this.entity,
      });
      // If user has permission to create on selected taxonomy, check if have to publish
      if (canCreateOnTaxonomy && !canPublishOnTaxonomy) {
        this.draft.autopublish = false;
        this.autopublishDisabled = true;
        this.autopublishErrorReason = 'category';
      }
    },
    async onFileSelected({ file }) {
      const title = await getImageDescription(file);
      if (title && !this.draft.title) {
        this.$set(this.draft, 'title', title);
      }
      const author = await getImageAuthor(file);
      if (author && !this.draft.signatureAuthorName) {
        this.$set(this.draft, 'signatureAuthorName', author);
      }
      this.$nextTick(() => {
        if (this.$refs.form) {
          this.$refs.form.validate();
        }
      });
    },
  },
  async mounted() {
    if (!this.autoPublish) {
      return;
    }
    // Load default autopublish state prop value
    const canPublish = await this.can('publish', {
      entity: 'content',
    });
    if (!canPublish) {
      this.draft.autopublish = false;
      this.autopublishDisabled = true;
      this.autopublishErrorReason = 'content';
    }
    const canPublishImage = await this.can('publish', {
      entity: 'content',
      type: this.entity,
    });
    if (!canPublishImage) {
      this.draft.autopublish = false;
      this.autopublishDisabled = true;
      this.autopublishErrorReason = this.entity;
    }
  },
};
</script>
